import {create} from "zustand";

/**
 * @interface Notificacion
 * @description Define la estructura de una notificación individual mostrada en la UI.
 */
export interface Notificacion {
  /**
   * @property {number} id - Identificador único de la notificación, generado con timestamp + aleatorio.
   */
  id: number;
  /**
   * @property {string} mensaje - El texto a mostrar dentro de la notificación.
   */
  mensaje: string;
  /**
   * @property {"success" | "error" | "info" | "warning"} gravedad - Nivel de severidad de la
   * notificación; controla el color y el icono que se muestra en la UI.
   */
  gravedad: "success" | "error" | "info" | "warning";
}

/**
 * @interface NotificacionState
 * @description Define el estado y las acciones para el store de notificaciones.
 * @property {boolean} abrir - Controla si la notificación está visible.
 * @property {string} mensaje - El mensaje a mostrar en la notificación.
 * @property {"success" | "error" | "info" | "warning"} gravedad - El nivel de severidad de la notificación (controla el color y el icono).
 * @property {(mensaje: string, gravedad?: "success" | "error" | "info" | "warning") => void} showNotification - Acción para mostrar una nueva notificación.
 * @property {() => void} cerrarNotificacion - Acción para cerrar la notificación actual.
 */
interface NotificacionState {
	notificaciones: Notificacion[];
	showNotification: (mensaje: string, gravedad?: Notificacion["gravedad"]) => void;
	closeNotification: (id: number) => void;
}

/**
 * Un store de Zustand para manejar notificaciones globales en la aplicación.
 * Permite agregar y eliminar notificaciones, cada una con un mensaje y un nivel de severidad.
 * Las notificaciones se almacenan en un array, permitiendo múltiples notificaciones simultáneas.
 *
 * @store
 * @returns {NotificacionState} El estado y las acciones del store de notificaciones.
 */
export const useNotificacionStore = create<NotificacionState>((set) => ({
	notificaciones: [],

	showNotification: (mensaje: string, gravedad: Notificacion["gravedad"] = "info") =>
		set((state) => {
			// Generar un ID único usando timestamp + contador para evitar colisiones
			// Usamos el timestamp multiplicado por 1000 y sumamos un número aleatorio entre 0-999
			const id = Date.now() * 1000 + Math.floor(Math.random() * 1000);
			return {
				notificaciones: [
					...state.notificaciones,
					{id, mensaje, gravedad},
				],
			};
		}),

	closeNotification: (id) =>
		set((state) => ({
			notificaciones: state.notificaciones.filter(
				(notificacion) => notificacion.id !== id
			),
		})),
}));
